// Profit tab — real profit calculation breakdown.
import { useEffect, useState } from "react";
import { AreaChart, Area, BarChart, Bar, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getProfit } from "@/lib/analytics";
import { fmtInt, fmtMoney, fmtPct, KpiCard } from "../components/KpiCard";
import { Card } from "./OverviewTab";
import SettingsDialog from "../components/SettingsDialog";
import { Button } from "@/components/ui/button";
import { Banknote, TrendingUp, Receipt, PiggyBank, Settings as SettingsIcon } from "lucide-react";

const COLOR = { primary: "#E91E63", emerald: "#22C55E", amber: "#F59E0B", rose: "#F43F5E", purple: "#A855F7", cyan: "#06B6D4", grey: "#71717a" };
const tip = { contentStyle: { background: "rgba(15, 15, 23, 0.95)", border: "1px solid rgba(91,91,214,0.4)", borderRadius: 8, fontSize: 12 } };

export default function ProfitTab({ range }) {
  const [data, setData] = useState(null);
  const [version, setVersion] = useState(0);
  useEffect(() => { getProfit(range).then(setData); }, [JSON.stringify(range), version]); // eslint-disable-line
  if (!data) return <div className="h-96 rounded-xl bg-card/40 border border-border animate-pulse" />;
  const t = data.totals;
  const breakdown = [
    { name: "COGS", value: t.cogs, color: COLOR.amber },
    { name: "Livrare", value: t.shipping_cost, color: COLOR.cyan },
    { name: "Ambalare", value: t.packaging_cost, color: COLOR.purple },
    { name: "Ad Spend", value: t.ad_spend, color: COLOR.rose },
    { name: "Retururi", value: t.return_cost, color: "#FB7185" },
    { name: "TVA", value: t.tax, color: COLOR.grey },
    { name: "Profit net", value: Math.max(0, t.net_profit), color: COLOR.emerald },
  ].filter((b) => b.value > 0);

  return (
    <div className="space-y-6" data-testid="analytics-profit">
      <div className="flex justify-end">
        <SettingsDialog onChanged={() => setVersion((v) => v + 1)} trigger={
          <Button variant="outline" size="sm" data-testid="profit-settings"><SettingsIcon className="h-3.5 w-3.5 mr-1.5" /> Setări costuri</Button>
        } />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Venit" value={t.revenue} format="money" icon={Banknote} color={COLOR.primary} />
        <KpiCard label="Profit Brut" value={t.gross_profit} format="money" icon={TrendingUp} color={COLOR.emerald} />
        <KpiCard label="Profit Net" value={t.net_profit} format="money" icon={PiggyBank} color={COLOR.emerald} />
        <KpiCard label="Marjă netă" value={t.net_margin_pct} format="pct" icon={Receipt} color={COLOR.amber} />
        <KpiCard label="Profit / comandă" value={t.profit_per_order} format="money" icon={PiggyBank} color={COLOR.emerald} />
        <KpiCard label="COGS" value={t.cogs} format="money" icon={Receipt} color={COLOR.amber} />
        <KpiCard label="Ad Spend" value={t.ad_spend} format="money" icon={Receipt} color={COLOR.rose} />
        <KpiCard label="TVA" value={t.tax} format="money" icon={Receipt} color={COLOR.grey} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Distribuție cost vs profit">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={breakdown} dataKey="value" innerRadius={60} outerRadius={95} paddingAngle={1.5}>
                {breakdown.map((b, i) => <Cell key={i} fill={b.color} />)}
              </Pie>
              <Tooltip {...tip} formatter={(v) => fmtMoney(v) + " RON"} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 mt-2">
            {breakdown.map((b) => (
              <div key={b.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2"><i className="h-2.5 w-2.5 rounded-full" style={{ background: b.color }} /> {b.name}</span>
                <span className="tabular-nums">{fmtMoney(b.value)} RON</span>
              </div>
            ))}
          </div>
        </Card>

        <div className="lg:col-span-2">
          <Card title="Profit zilnic (trend)">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={data.series}>
                <defs>
                  <linearGradient id="p-pf" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLOR.emerald} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={COLOR.emerald} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" stroke="#71717a" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#71717a" fontSize={10} />
                <Tooltip {...tip} formatter={(v) => fmtMoney(v) + " RON"} />
                <Area type="monotone" dataKey="profit" stroke={COLOR.emerald} strokeWidth={2} fill="url(#p-pf)" />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Profit pe județ">
          <div className="max-h-[380px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase text-muted-foreground sticky top-0 bg-card">
                <tr>
                  <th className="text-left py-2 px-2">Județ</th>
                  <th className="text-right">Comenzi</th>
                  <th className="text-right">Venit</th>
                  <th className="text-right">Cost</th>
                  <th className="text-right">Profit</th>
                  <th className="text-right pr-2">Marjă</th>
                </tr>
              </thead>
              <tbody>
                {data.by_county.map((r) => (
                  <tr key={r.county} className="border-t border-border/50 hover:bg-secondary/20">
                    <td className="py-1.5 px-2">{r.county}</td>
                    <td className="text-right tabular-nums">{fmtInt(r.orders)}</td>
                    <td className="text-right tabular-nums">{fmtMoney(r.revenue)}</td>
                    <td className="text-right tabular-nums text-muted-foreground">{fmtMoney(r.cogs)}</td>
                    <td className="text-right tabular-nums font-semibold text-emerald-500">{fmtMoney(r.profit)}</td>
                    <td className="text-right tabular-nums pr-2">{r.margin_pct.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Profit pe furnizor">
          <div className="max-h-[380px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase text-muted-foreground sticky top-0 bg-card">
                <tr>
                  <th className="text-left py-2 px-2">Furnizor</th>
                  <th className="text-right">Unități</th>
                  <th className="text-right">Venit</th>
                  <th className="text-right">Cost</th>
                  <th className="text-right">Profit</th>
                  <th className="text-right pr-2">Marjă</th>
                </tr>
              </thead>
              <tbody>
                {data.by_supplier.map((r) => (
                  <tr key={r.supplier} className="border-t border-border/50 hover:bg-secondary/20">
                    <td className="py-1.5 px-2">{r.supplier}</td>
                    <td className="text-right tabular-nums">{fmtInt(r.units)}</td>
                    <td className="text-right tabular-nums">{fmtMoney(r.revenue)}</td>
                    <td className="text-right tabular-nums text-muted-foreground">{fmtMoney(r.cogs)}</td>
                    <td className="text-right tabular-nums font-semibold text-emerald-500">{fmtMoney(r.profit)}</td>
                    <td className="text-right tabular-nums pr-2">{r.margin_pct.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
